/**
 * Drop Generator — Hour Optimizer contracts (Phase 11, formula in Phase 16).
 * A campaign's day as 24 recipient-local hour buckets with the tracker's
 * counters, ONE score per bucket from a Flow-Optimizer-shaped formula
 * (groups with their own date range, weighted), and the honest "unranked"
 * state for hours that have not seen enough traffic to be trusted (never
 * scored as bad).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenRuleHit } from "./rules/rules.types";
import type { HourTagVerdict } from "../tag-rules/hours/hour-tags.types";
import { FLOW_FORMULA_METRIC_IDS } from "../formula/catalog";
import type {
  FormulaBlocksBody,
  FormulaGroupMode,
  FormulaSlidersBody,
} from "../voluum-flow-optimizer.types";

/** The metrics an hour (or text) formula may use — the Flow Optimizer's
 *  whole formula catalog, in its ids and units (cr = conversions ÷ visits %,
 *  cv = conversions ÷ clicks %, epc = revenue ÷ clicks…). Both trackers'
 *  bags are completed with `deriveFlowMetrics`, so every one has a value. */
export const DROP_GEN_HOUR_FORMULA_METRICS: readonly string[] =
  FLOW_FORMULA_METRIC_IDS;
export type DropGenHourFormulaMetric = string;

/** Date ranges an hour group may read — whole days only (a rolling-hours
 *  window would give partial hour buckets); `custom` adds YYYY-MM-DD bounds. */
export const DROP_GEN_HOUR_DATE_RANGES = [
  "today",
  "yesterday",
  "last-3-days",
  "last-7-days",
  "last-14-days",
  "this-month",
  "last-30-days",
  "last-month",
  "this-year",
  "last-year",
] as const;
export type DropGenHourDateRange = (typeof DROP_GEN_HOUR_DATE_RANGES)[number];

export const DROP_GEN_HOUR_MAX_GROUPS = 6;

/** One formula group: the Flow Optimizer's shape minus its layer scoping. */
export interface DropGenHourFormulaGroup {
  id: string;
  name: string;
  /** A DROP_GEN_HOUR_DATE_RANGES preset, or `custom`. */
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  mode: FormulaGroupMode;
  sliders?: FormulaSlidersBody;
  blocks?: FormulaBlocksBody;
  /** Canonical text form; the scorer re-derives it from the body anyway. */
  expression: string;
  /** Runtime only, never saved: the ranker's highest value of each lowest() metric. */
  lowestMax?: Record<string, number>;
}

export interface DropGenHourFormula {
  /** 1..DROP_GEN_HOUR_MAX_GROUPS; groups[0] is the "first range" the default "Not enough data" rule reads. */
  groups: DropGenHourFormulaGroup[];
  /** Group id → weight; normalized to 100 at scoring (all-zero = equal). */
  groupWeights: Record<string, number>;
}

export interface DropGenHourScoreContribution {
  metric: string;
  weight: number;
  value: number;
  contribution: number;
  /** A "Lowest" row: contribution = (max − value) × weight; no max = −value. */
  lowest?: boolean;
  max?: number;
}

/** What one group contributed to one hour's score. */
export interface DropGenHourScoreBreakdownRow {
  groupId: string;
  groupName: string;
  rangeKey: string;
  groupWeight: number;
  rawScore: number;
  weightedScore: number;
  expression: string;
  /** The range had no row for this hour — scored 0 and said so, never hidden. */
  noData: boolean;
  /** Sliders: each metric's value × its exact coefficient (they sum to rawScore). */
  contributions?: DropGenHourScoreContribution[];
  /** Blocks: each sub-formula's value × its normalized weight. */
  subformulaContributions?: Array<{
    subformulaId: string;
    name: string;
    expression: string;
    value: number;
    weight?: number;
    contribution: number;
  }>;
}

export interface DropGenHourBucket {
  /** 0..23, recipient-local. */
  hour: number;
  visits: number;
  uniqueVisits: number;
  clicks: number;
  uniqueClicks: number;
  conversions: number;
  revenue: number;
  cost: number;
  /** Every Flow Optimizer metric of the hour on the primary range. */
  metrics?: Record<string, number>;
  /** The formula's score; null while unranked. */
  score: number | null;
  /** 1 = best ranked hour; null while unranked. */
  rank: number | null;
  /** Excluded by a rule ("Not enough data" by default) or no data in any range — never "bad". */
  unranked: boolean;
  /** Per-group math behind the score; absent while unranked. */
  breakdown?: DropGenHourScoreBreakdownRow[];
  /** Phase 19 — what the hour rules said (fired or could not judge). */
  ruleHits?: DropGenRuleHit[];
  /** Hour tags (Tag Rules › Hours) of this hour — only when an hour rule tests a tag. */
  tags?: Record<string, HourTagVerdict>;
}

export interface DropGenHourProfile {
  countryKey: string;
  campaignWiserId: number;
  provider: "VOLUUM" | "BINOM" | "TRES_COMMAS";
  /** The primary range key (groups[0]) — kept for older readers. */
  lookbackPreset: string;
  primaryRangeKey: string;
  /** The buckets' timezone — the country's recipient timezone. */
  timezone: string;
  scoreKind: "formula";
  /** The formula that produced the scores (the default when none was given). */
  formula: DropGenHourFormula;
  /** Always 24 entries, hour 0..23. */
  buckets: DropGenHourBucket[];
  rankedHours: number;
  fetchedAt: string;
  /** Hour tags: colours, names the rules test that no enabled tag rule has,
   *  and true when the tags could not be read (every tag condition unknown). */
  tagColors?: Record<string, string>;
  missingTags?: string[];
  tagsUnavailable?: boolean;
}
